'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { useEffect, useState } from 'react';
import { theme } from '../theme';
import type { Locale } from '../theme';
import { LocuslyMark, Container } from '../primitives';
import { LangSwitcher } from '../lang-switcher';
import type { Strings } from '../strings';

export function Nav({
  s,
  locale,
  bookCallEnabled = false,
}: {
  s: Strings;
  locale: Locale;
  bookCallEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Close the menu on Escape and lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const menuLabel = locale === 'pt' ? 'Menu' : 'Menu';

  return (
    <>
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'rgba(242,235,223,0.85)',
        backdropFilter: 'saturate(140%) blur(10px)',
        WebkitBackdropFilter: 'saturate(140%) blur(10px)',
        borderBottom: `1px solid ${theme.c.border}`,
      }}
    >
      <Container>
        <nav
          aria-label="Primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 72,
            gap: 24,
          }}
        >
          <Link
            href={`/${locale}` as Route}
            style={{ textDecoration: 'none' }}
            aria-label="Locusly home"
          >
            <LocuslyMark size={22} />
          </Link>

          <div
            className="lp-nav-links"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 28,
              fontFamily: theme.fonts.body,
              fontSize: 14,
              color: theme.c.fgMuted,
            }}
          >
            {s.nav.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="lp-nav-link"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="lp-nav-lang-desk">
              <LangSwitcher current={locale} compact />
            </div>
            <button
              type="button"
              className="lp-nav-burger"
              aria-label={menuLabel}
              aria-expanded={open}
              aria-controls="lp-mobile-menu"
              onClick={() => setOpen((o) => !o)}
            >
              <span aria-hidden="true" className={open ? 'is-open' : ''}>
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </nav>
      </Container>
    </header>

    {/* MOBILE MENU OVERLAY -rendered as a sibling of <header>, NOT a descendant,
        because <header> uses backdrop-filter which creates a containing block
        for position:fixed descendants. */}
    <div
      id="lp-mobile-menu"
      className={`lp-nav-mobile-menu${open ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
      aria-label={menuLabel}
    >
        <div className="lp-nav-mobile-inner">
          <ul className="lp-nav-mobile-list">
            {s.nav.links.map((l, i) => (
              <li key={l.href} style={{ ['--i' as string]: i }}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="lp-nav-mobile-link"
                >
                  {l.label}
                  <span aria-hidden="true" className="lp-nav-mobile-arrow">→</span>
                </a>
              </li>
            ))}
          </ul>

          <div className="lp-nav-mobile-foot">
            <LangSwitcher current={locale} compact />
            {bookCallEnabled ? (
              <a
                href={`/${locale}/book-call`}
                onClick={() => setOpen(false)}
                className="lp-btn-primary lp-nav-mobile-cta"
              >
                {s.nav.cta}
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
        .lp-nav-burger {
          display: none;
          background: transparent;
          border: 1px solid ${theme.c.borderStrong};
          border-radius: ${theme.radii.pill}px;
          width: 42px;
          height: 38px;
          cursor: pointer;
          padding: 0;
          align-items: center;
          justify-content: center;
        }
        .lp-nav-burger > span {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 18px;
          height: 14px;
          position: relative;
        }
        .lp-nav-burger > span > span {
          position: absolute;
          left: 0;
          right: 0;
          height: 1.5px;
          background: ${theme.c.fg};
          border-radius: 999px;
          transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 200ms cubic-bezier(0.22, 1, 0.36, 1),
            top 280ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lp-nav-burger > span > span:nth-child(1) { top: 0; }
        .lp-nav-burger > span > span:nth-child(2) { top: 50%; transform: translateY(-50%); }
        .lp-nav-burger > span > span:nth-child(3) { top: 100%; transform: translateY(-100%); }

        .lp-nav-burger > span.is-open > span:nth-child(1) {
          top: 50%;
          transform: translateY(-50%) rotate(45deg);
        }
        .lp-nav-burger > span.is-open > span:nth-child(2) {
          opacity: 0;
        }
        .lp-nav-burger > span.is-open > span:nth-child(3) {
          top: 50%;
          transform: translateY(-50%) rotate(-45deg);
        }

        .lp-nav-mobile-menu {
          position: fixed;
          top: 72px;
          right: 0;
          bottom: 0;
          left: 0;
          background-color: ${theme.c.bg};
          z-index: 49;
          opacity: 0;
          visibility: hidden;
          transform: translate3d(0, -8px, 0);
          transition: opacity 280ms cubic-bezier(0.22, 1, 0.36, 1),
            visibility 280ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
          overflow-y: auto;
        }
        .lp-nav-mobile-menu.is-open {
          opacity: 1;
          visibility: visible;
          transform: translate3d(0, 0, 0);
          pointer-events: auto;
        }

        .lp-nav-mobile-inner {
          padding: 32px 24px 40px;
          display: flex;
          flex-direction: column;
          height: 100%;
          max-width: 720px;
          margin: 0 auto;
          width: 100%;
        }

        .lp-nav-mobile-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        .lp-nav-mobile-list > li {
          border-bottom: 1px solid ${theme.c.border};
          opacity: 0;
          transform: translate3d(0, 12px, 0);
          transition: opacity 360ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 360ms cubic-bezier(0.22, 1, 0.36, 1);
          transition-delay: calc(60ms * var(--i));
        }
        .lp-nav-mobile-menu.is-open .lp-nav-mobile-list > li {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .lp-nav-mobile-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px 4px;
          font-family: ${theme.fonts.display};
          font-size: 26px;
          font-weight: 600;
          letter-spacing: -0.6px;
          color: ${theme.c.fg};
          text-decoration: none;
        }
        .lp-nav-mobile-arrow {
          font-family: ${theme.fonts.body};
          color: ${theme.c.fgFaint};
          font-size: 18px;
          transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
            color 240ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lp-nav-mobile-link:hover .lp-nav-mobile-arrow,
        .lp-nav-mobile-link:active .lp-nav-mobile-arrow {
          color: ${theme.c.accent};
          transform: translate3d(4px, 0, 0);
        }

        .lp-nav-mobile-foot {
          margin-top: auto;
          padding-top: 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .lp-nav-mobile-cta {
          background: ${theme.c.accent};
          color: ${theme.c.accentFg};
          font-family: ${theme.fonts.body};
          font-size: 15px;
          font-weight: 500;
          padding: 12px 22px;
          border-radius: ${theme.radii.pill}px;
          text-decoration: none;
          box-shadow: 0 12px 24px -10px rgba(200,85,61,0.45);
        }

        @media (max-width: 960px) {
          .lp-nav-burger { display: inline-flex; }
          .lp-nav-links { display: none !important; }
          .lp-nav-lang-desk { display: none !important; }
        }
        @media (min-width: 961px) {
          .lp-nav-mobile-menu { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lp-nav-mobile-menu,
          .lp-nav-mobile-list > li,
          .lp-nav-burger > span > span,
          .lp-nav-mobile-arrow {
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
}
